import type { CreateOrderParams, ModelOrder, PaymentResult, PayOrderParams } from '@/types/model'
import { httpClient } from '@/utils/request'

const MODEL_BASE_URL = '/models'
const ORDER_BASE_URL = '/orders'

export interface ModelOrderItem {
  id: string | number
  orderNo?: string
  modelId?: string | number
  modelName?: string
  modelCover?: string
  payablePoints?: number
  status?: string
  purchaseStatus?: string
  createdAt?: string
  paidAt?: string
  failReason?: string
  // snake_case 兼容字段（后端原始响应）
  order_no?: string
  model_id?: string | number
  model_title?: string
  model_name?: string
  model_cover?: string
  model_format?: string
  modelFormat?: string
  file_size?: string | number
  fileSize?: string | number
  original_price?: number
  originalPrice?: number
  paid_price?: number
  paidPrice?: number
  orderNumber?: string
  orderStatus?: string
  created_at?: string
  expire_at?: string
  expireAt?: string
  paid_at?: string
  download_count?: number
  downloadCount?: number
  isFree?: boolean
}

export interface DownloadRecordItem {
  id: string | number
  modelId?: string | number
  model_id?: string | number // 后端实际返回
  modelName?: string // 保留旧字段兼容
  model_title?: string // 后端实际返回
  modelTitle?: string // camelCase 兼容
  model_cover?: string // 后端返回
  modelCover?: string // camelCase 兼容
  downloadAt?: string
  downloaded_at?: string // 后端实际返回
}

/**
 * 生成模型交易幂等性Key
 * 格式: {业务类型}_{用户标识}_{模型/订单ID}_{时间戳}_{随机后缀}
 */
export function generateModelIdempotencyKey(
  type: 'model_order' | 'model_pay',
  userId: string | number,
  businessId: string | number,
): string {
  const timestamp = Date.now()
  const randomSuffix = Math.random().toString(36).substring(2, 6)
  // UUIDs are 36 chars long. Using them fully would exceed backend's 100 char limit for idempotency_key.
  // We extract the first 8 characters.
  const shortUserId = String(userId).replace(/-/g, '').substring(0, 8)
  const shortBizId = String(businessId).replace(/-/g, '').substring(0, 8)
  const prefix = type === 'model_order' ? 'mo' : 'mp'
  return `${prefix}_${shortUserId}_${shortBizId}_${timestamp}_${randomSuffix}`
}

/**
 * 创建模型购买订单
 * @description 必须传递 idempotencyKey 防止重复下单
 */
export async function createModelOrder(
  id: string | number,
  data: CreateOrderParams & { idempotencyKey?: string },
): Promise<ModelOrder> {
  const headers: Record<string, string> = {}
  if (data.idempotencyKey) {
    headers['Idempotency-Key'] = data.idempotencyKey
  }
  return httpClient.post<ModelOrder>(`${MODEL_BASE_URL}/${id}/orders`, data, { headers })
}

/**
 * 支付模型订单
 * @description 必须传递 idempotencyKey 防止重复支付
 */
export async function payModelOrder(
  id: string,
  data: PayOrderParams & { idempotencyKey?: string },
): Promise<PaymentResult> {
  const headers: Record<string, string> = {}
  if (data.idempotencyKey) {
    headers['Idempotency-Key'] = data.idempotencyKey
  }
  return httpClient.post<PaymentResult>(`${ORDER_BASE_URL}/${id}/pay`, data, { headers })
}

export async function getModelOrders(params?: Record<string, unknown>): Promise<{ data: ModelOrderItem[], count: number }> {
  const page = (params?.page as number) || 1
  const pageSize = (params?.pageSize as number) || 20

  const queryParams: Record<string, string | number> = { page, pageSize }
  // 注意：后端暂时不支持 keyword 和 dateRange 高级检索，目前只能发 page 和 pageSize

  return httpClient.get<{ data: ModelOrderItem[], count: number }>('/users/me/orders/models', { params: queryParams })
}

/**
 * 补充获取前端我的下载记录接口
 */
export async function getMyDownloads(params?: Record<string, unknown>): Promise<{ data: DownloadRecordItem[], count: number }> {
  const page = (params?.page as number) || 1
  const pageSize = (params?.pageSize as number) || 20
  const queryParams: Record<string, string | number> = { page, pageSize }

  return httpClient.get<{ data: DownloadRecordItem[], count: number }>('/users/me/downloads', { params: queryParams })
}

// ── 售后接口 ──────────────────────────────────────────────

/** 售后问题提交请求（对齐后端 AfterSalesRequest） */
export interface AfterSalesRequest {
  model_id: string // UUID
  order_id?: string // UUID, 可选
  issue_type: string // 问题类型
  description: string // 问题描述（max 2000）
  contact?: string // 联系方式（max 100）
}

/** 售后提交响应（对齐后端 AfterSalesResponse） */
export interface AfterSalesResponse {
  ticket_id: string
  status: string // 默认 "pending"
  message: string
}

/**
 * 提交售后问题
 * @description POST /users/me/after-sales
 */
export async function submitAfterSales(data: AfterSalesRequest): Promise<AfterSalesResponse> {
  return httpClient.post<AfterSalesResponse>('/users/me/after-sales', data)
}
